import { FormEvent, useState } from 'react';
import axios from 'axios';

import { ToggleSwitch } from '../../shared/ToggleSwitch/ToggleSwitch';
import { SpeechBubble } from '../../shared';

import { toHTMLDate } from '../../utils/dates';
import { CSV_UTF8_MIME_TYPE, download } from '../../utils/download';
import { IAP_PRICING_BASE_PATH } from '../../utils/pathsConstants';
import { handleError, toCsv } from '../../utils/utils';

import { ApiAvailableStore } from '../../types/enums/store.enum';

import styles from './ExportSKUsForm.module.scss';

type ExportSKUsRes = {
    store: ApiAvailableStore;
    products: { sku: string; price: number | null; error: string }[];
};

export const ExportSKUsForm = () => {
    const [errors, setErrors] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOnlyActive, setIsOnlyActive] = useState<boolean>(true);

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setErrors([]);

        const data = new FormData(event.target as HTMLFormElement);

        const checkedStores = [
            ApiAvailableStore.APPLE,
            ApiAvailableStore.GOOGLE,
        ].filter((store) => data.get(store) === 'on');

        if (checkedStores.length === 0) {
            setErrors(['At least one store must be selected']);
            setIsLoading(false);
            return;
        }

        try {
            await Promise.all(
                checkedStores.map(async (store) => {
                    const {
                        data: { products },
                    } = await axios.get<ExportSKUsRes>(
                        `/api/${IAP_PRICING_BASE_PATH}/inapppurchases/list-skus/${store}`,
                        {
                            params: {
                                onlyActive: isOnlyActive,
                            },
                        },
                    );
                    const csv = toCsv(products);

                    if (!csv) {
                        setErrors((errors) => [
                            ...errors,
                            `Unable to generate CSV file for ${store} store`,
                        ]);
                        return;
                    }

                    const filename = `[${toHTMLDate(
                        new Date(),
                    )}] - ${store} skus.csv`;

                    download(csv, CSV_UTF8_MIME_TYPE, filename);
                }),
            );
        } catch (error) {
            const msg = handleError("Couldn't download CSV", error, false);

            setErrors((errors) => [...errors, msg]);
        }

        setIsLoading(false);
    };

    const getCheckboxId = (store: ApiAvailableStore): string => {
        return `export-skus-${store}`;
    };

    return (
        <>
            <SpeechBubble
                style={{
                    justifyContent: 'left',
                    marginTop: 10,
                    marginBottom: 20,
                }}
            >
                Both Apple and Google USD prices are taken from Google Play.
            </SpeechBubble>
            <form onSubmit={onSubmit} className={styles.Form}>
                <div>
                    <div className={styles.ToggleWrapper}>
                        <ToggleSwitch
                            checked={isOnlyActive}
                            onToggle={() =>
                                setIsOnlyActive((currentValue) => !currentValue)
                            }
                            disabled={isLoading}
                        />
                        <span>
                            {isOnlyActive ? 'Export only active' : 'Export all'}
                        </span>
                    </div>
                    <input
                        type="checkbox"
                        id={getCheckboxId(ApiAvailableStore.APPLE)}
                        name={ApiAvailableStore.APPLE}
                    />
                    <label htmlFor={getCheckboxId(ApiAvailableStore.APPLE)}>
                        {' '}
                        Apple Store
                    </label>
                    <input
                        type="checkbox"
                        id={getCheckboxId(ApiAvailableStore.GOOGLE)}
                        name={ApiAvailableStore.GOOGLE}
                    />
                    <label htmlFor={getCheckboxId(ApiAvailableStore.GOOGLE)}>
                        {' '}
                        Google Play Store
                    </label>
                </div>

                <button type="submit" className="btn" disabled={isLoading}>
                    {isLoading ? 'Exporting...' : 'Export'}
                </button>
            </form>
            {errors?.length ? (
                <pre className={styles.ErrorMessage}>{errors.join('\n')}</pre>
            ) : null}
        </>
    );
};
